import {QueryRouteQueryType} from '@github-ui/react-core/future/data-router-types'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {CopilotChatReference, CopilotDeferredPayload} from '@github-ui/copilot-chat-types'
import type {ReadOnlyLegacyChatPayload} from '@github-ui/agent-sessions/components/legacy-chat/ReadOnlyLegacyChat'

export interface ChatPayload extends ReadOnlyLegacyChatPayload {
  refs?: CopilotChatReference[]
  deferredPayloadUrl?: undefined
}

type InitialChatPayload =
  | ChatPayload
  | (Omit<ChatPayload, keyof CopilotDeferredPayload | 'deferredPayloadUrl'> &
      Partial<CopilotDeferredPayload> & {deferredPayloadUrl: string})

export async function fetchCopilotChatPayload(): Promise<ChatPayload> {
  const resp = await verifiedFetchJSON('/github-copilot/chat')
  if (!resp.ok) {
    throw new Error(`Failed to fetch chat provider: ${resp.statusText}`)
  }
  const payload: InitialChatPayload = await resp.json()
  if (payload.deferredPayloadUrl === undefined) return payload

  // Providers seed reducer state on mount, so publish account eligibility only after it is hydrated.
  const deferredResponse = await verifiedFetchJSON(payload.deferredPayloadUrl)
  if (!deferredResponse.ok) {
    throw new Error(`Failed to fetch deferred chat provider: ${deferredResponse.status}`)
  }
  const deferredPayload: CopilotDeferredPayload = await deferredResponse.json()
  return {...payload, ...deferredPayload, deferredPayloadUrl: undefined}
}

export const copilotChatPayloadRouteQuery = {
  queryName: 'copilotChatPayload',
  queryFn: async (): Promise<ChatPayload> => fetchCopilotChatPayload(),
  type: QueryRouteQueryType.Deferred,
} as const

export const copilotChatPayloadBlockingRouteQuery = {
  queryName: 'copilotChatPayload',
  queryFn: async (): Promise<ChatPayload> => fetchCopilotChatPayload(),
  type: QueryRouteQueryType.Blocking,
} as const

export const copilotChatPayloadDeferredRouteQuery = {
  queryName: 'copilotChatPayload',
  queryFn: async (): Promise<ChatPayload> => fetchCopilotChatPayload(),
  retry: false,
  type: QueryRouteQueryType.Deferred,
} as const
